// ════════════════════════════════════════════════════════════════════════
//  Der gespeicherte Stand der Führung
//
//  Serverseitig und nicht im Browser: die Lehrkraft lädt zu Hause ein und macht
//  in der Schule weiter, und der Rechner im Klassenraum ist nicht ihrer. Für die
//  Redaktion gilt dasselbe an drei Geräten.
//
//  JE PERSON UND JE APP
//
//  Der Schlüssel der Tabelle ist `(user_id, app)`. Dieselbe Person kann beides
//  sein - ein Plattformkonto in der Redaktion und ein Lehrkraftprofil in der
//  Verwaltung liegen in derselben Auth-Tabelle. Ohne `app` verschöbe die eine
//  Führung das Lesezeichen der anderen.
//
//  Kein `eq("user_id", ...)`: die Policy lässt ohnehin nur die eigene Zeile
//  durch. Doppelt zu filtern verschleiert nur, wo die Grenze wirklich liegt.
//  Nach `app` wird dagegen sehr wohl gefiltert - das ist keine Rechtefrage,
//  sondern die Frage, welche der eigenen Zeilen gemeint ist.
//
//  Geschrieben wird ausschliesslich beim Weiterschalten, Weglegen und
//  Zurückholen. Kein Autospeichern und kein Zählen: die Führung schreibt genau
//  eine Zahl fort, wie weit DIESE Person ist.
// ════════════════════════════════════════════════════════════════════════

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys, type OttoClient } from "@otto/plattform";
import type { TourState } from "./tour.js";

/** Welche Führung. Der Wert steht so in der Spalte `onboarding.app`. */
export type TourApp = "verwaltung" | "admin";

/**
 * Der Stand, oder `null` – und `null` ist ein Zustand, kein Fehler: wer noch
 * nie angemeldet war, hat keine Zeile und steht damit am Anfang.
 */
export function useTourState(client: OttoClient, userId: string, app: TourApp) {
  return useQuery({
    queryKey: queryKeys.onboarding(userId, app),
    enabled: userId !== "",
    queryFn: async (): Promise<TourState | null> => {
      const { data, error } = await client
        .from("onboarding")
        .select("step, done_at")
        .eq("app", app)
        .maybeSingle();
      if (error) throw error;
      // Die Attrappen der Komponententests liefern für jede Tabelle dasselbe
      // leere Ergebnis; ohne diese Prüfung entstünde daraus ein Stand mit
      // `step: undefined`, der sich wie ein echter läse.
      if (!data || typeof data !== "object" || !("step" in data)) return null;
      return { step: data.step, doneAt: data.done_at };
    },
  });
}

/**
 * Stand schreiben. Ein Upsert, weil die erste Zeile beim ersten Handgriff
 * entsteht - der Einladungsweg legt bewusst keine an.
 */
export function useSaveTour(client: OttoClient, userId: string, app: TourApp) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { step: string; doneAt: string | null }) => {
      const { error } = await client
        .from("onboarding")
        .upsert(
          { user_id: userId, app, step: input.step, done_at: input.doneAt },
          { onConflict: "user_id,app" },
        );
      if (error) throw error;
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: queryKeys.onboarding(userId, app) }),
  });
}
